import { Fragment, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";
import swal from "sweetalert";
import { toast } from "react-toastify";
import config from "../../../../config.json";
import PageTitle from "../../layouts/PageTitle";
import {
  archiveLostFoundItem,
  createLostFoundItem,
  fetchLostFoundItems,
  updateLostFoundItem,
  updateLostFoundItemStatus,
} from "../../../services/LostFoundService";
import { useTranslation } from "react-i18next";
import "react-datepicker/dist/react-datepicker.css";
import "./LostFound.css";

const PAGE_SIZE = 8;

const emptyForm = {
  title: "",
  description: "",
  foundDate: new Date().toISOString().slice(0, 10),
  status: "found",
  image: null,
};

const getTodayDateString = () => new Date().toISOString().slice(0, 10);

const toLocalDateKey = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const toDateInputValue = (value) => {
  if (!value) return getTodayDateString();
  const key = toLocalDateKey(value);
  return key || getTodayDateString();
};

const isFutureFoundDate = (dateStr) => {
  if (!dateStr) return false;
  return dateStr > getTodayDateString();
};

const formatDate = (value, locale) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  try {
    return new Intl.DateTimeFormat(locale || "en", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
};

const truncateText = (value = "", max = 60) => {
  const text = String(value || "").trim();
  if (!text) return "—";
  if (text.length <= max) return text;
  return `${text.slice(0, max)}…`;
};

const formatItemCode = (id) => {
  if (!id) return "LF-0000";
  const hex = String(id).replace(/[^a-fA-F0-9]/g, "").slice(-4) || "0";
  const num = Number.parseInt(hex, 16) % 10000;
  return `LF-${String(num).padStart(4, "0")}`;
};

const startOfWeek = () => {
  const now = new Date();
  const day = now.getDay();
  const diff = day === 0 ? 6 : day - 1;
  const monday = new Date(now);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(now.getDate() - diff);
  return monday;
};

const sortItems = (list, sortBy) => {
  const [field, dir] = sortBy.split("-");
  return [...list].sort((a, b) => {
    let cmp = 0;
    if (field === "title") {
      cmp = String(a.title || "").localeCompare(String(b.title || ""), undefined, {
        sensitivity: "base",
      });
    } else if (field === "foundDate") {
      cmp = new Date(a.foundDate || 0).getTime() - new Date(b.foundDate || 0).getTime();
    } else if (field === "status") {
      cmp = String(a.status || "").localeCompare(String(b.status || ""));
    }
    return dir === "desc" ? -cmp : cmp;
  });
};

function ItemFormModal({ open, item, onClose, onSaved, t }) {
  const isEdit = Boolean(item?._id);
  const [form, setForm] = useState(emptyForm);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [dateError, setDateError] = useState("");

  useEffect(() => {
    if (!open) return;
    if (item) {
      setForm({
        title: item.title || "",
        description: item.description || "",
        foundDate: toDateInputValue(item.foundDate),
        status: item.status === "returned" ? "returned" : "found",
        image: null,
      });
      setPreview(
        item.image ? `${config.UPLOADS_SERVER}/lost-found/${item.image}` : null
      );
    } else {
      setForm({ ...emptyForm, foundDate: getTodayDateString() });
      setPreview(null);
    }
    setSubmitting(false);
    setDateError("");
  }, [open, item]);

  if (!open) return null;

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    setForm((prev) => ({ ...prev, image: file || null }));
    setPreview(file ? URL.createObjectURL(file) : null);
  };

  const handleDateChange = (value) => {
    setForm((prev) => ({ ...prev, foundDate: value }));
    setDateError(isFutureFoundDate(value) ? t("lostFoundDateFutureError") : "");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.title.trim()) {
      toast.warning(t("lostFoundTitleRequired"));
      return;
    }
    if (isFutureFoundDate(form.foundDate)) {
      setDateError(t("lostFoundDateFutureError"));
      toast.error(t("lostFoundDateFutureError"));
      return;
    }

    setSubmitting(true);
    try {
      if (isEdit) {
        await updateLostFoundItem(item._id, form);
        toast.success(t("lostFoundUpdated"));
      } else {
        await createLostFoundItem(form);
        toast.success(t("lostFoundCreated"));
      }
      onSaved();
      onClose();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          (isEdit ? t("lostFoundUpdateError") : t("lostFoundCreateError"))
      );
    } finally {
      setSubmitting(false);
    }
  };

  const clearNewImage = () => {
    setForm((prev) => ({ ...prev, image: null }));
    setPreview(
      item?.image ? `${config.UPLOADS_SERVER}/lost-found/${item.image}` : null
    );
  };

  return createPortal(
    <div className="lf-modal-backdrop" onClick={onClose}>
      <div
        className="lf-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lf-modal-heading"
      >
        <div className="lf-modal__head">
          <div className="lf-modal__head-main">
            <span className="lf-modal__head-icon" aria-hidden="true">
              <i className={`fa ${isEdit ? "fa-pencil" : "fa-plus"}`} />
            </span>
            <div>
              <h2 id="lf-modal-heading">
                {isEdit ? t("lostFoundEditItem") : t("lostFoundAddItem")}
              </h2>
              <p>{isEdit ? t("lostFoundEditFormHint") : t("lostFoundFormHint")}</p>
            </div>
          </div>
          <button type="button" className="lf-modal__close" onClick={onClose} aria-label={t("cancel")}>
            <i className="fa fa-times" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="lf-modal__body">
            <div className="lf-modal__field">
              <label htmlFor="lf-modal-title">
                <i className="fa fa-tag" aria-hidden="true" />
                {t("lostFoundItemName")}
              </label>
              <input
                id="lf-modal-title"
                className="lf-modal__input"
                value={form.title}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                placeholder={t("lostFoundItemNamePlaceholder")}
                autoFocus
              />
            </div>

            <div className="lf-modal__grid">
              <div className="lf-modal__field">
                <label htmlFor="lf-modal-date">
                  <i className="fa fa-calendar" aria-hidden="true" />
                  {t("lostFoundDate")}
                </label>
                <div className={`lf-modal__date-wrap ${dateError ? "is-error" : ""}`}>
                  <i className="fa fa-calendar lf-modal__date-icon" aria-hidden="true" />
                  <input
                    id="lf-modal-date"
                    className="lf-modal__input lf-modal__input--date"
                    type="date"
                    max={getTodayDateString()}
                    value={form.foundDate}
                    onChange={(e) => handleDateChange(e.target.value)}
                    onClick={(e) => {
                      if (typeof e.currentTarget.showPicker === "function") {
                        try {
                          e.currentTarget.showPicker();
                        } catch {
                          /* ignore */
                        }
                      }
                    }}
                  />
                </div>
                {dateError && <p className="lf-modal__error">{dateError}</p>}
              </div>

              <div className="lf-modal__field">
                <span className="lf-modal__label">
                  <i className="fa fa-flag" aria-hidden="true" />
                  {t("status")}
                </span>
                <div className="lf-modal__chips" role="group" aria-label={t("status")}>
                  <button
                    type="button"
                    className={`lf-modal__chip is-found ${form.status === "found" ? "is-active" : ""}`}
                    onClick={() => setForm((prev) => ({ ...prev, status: "found" }))}
                  >
                    <span className="lf-modal__chip-dot" aria-hidden="true" />
                    {t("lostFoundFound")}
                  </button>
                  <button
                    type="button"
                    className={`lf-modal__chip is-returned ${form.status === "returned" ? "is-active" : ""}`}
                    onClick={() => setForm((prev) => ({ ...prev, status: "returned" }))}
                  >
                    <span className="lf-modal__chip-dot" aria-hidden="true" />
                    {t("lostFoundReturned")}
                  </button>
                </div>
              </div>
            </div>

            <div className="lf-modal__field">
              <label htmlFor="lf-modal-desc">
                <i className="fa fa-list-ul" aria-hidden="true" />
                {t("description")}
              </label>
              <textarea
                id="lf-modal-desc"
                className="lf-modal__input lf-modal__textarea"
                rows={3}
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                placeholder={t("lostFoundDescriptionPlaceholder")}
              />
            </div>

            <div className="lf-modal__field">
              <span className="lf-modal__label">
                <i className="fa fa-image" aria-hidden="true" />
                {t("lostFoundImage")}
              </span>
              <label className="lf-modal__upload" htmlFor="lf-modal-image">
                <span className="lf-modal__upload-icon" aria-hidden="true">
                  <i className="fa fa-cloud-upload" />
                </span>
                <span className="lf-modal__upload-text">
                  <strong>{t("lostFoundChooseImage")}</strong>
                  <small>{t("lostFoundImageHint")}</small>
                </span>
                <input id="lf-modal-image" type="file" accept="image/*" onChange={handleImageChange} hidden />
              </label>
              {preview && (
                <div className="lf-modal__preview">
                  <img src={preview} alt="" />
                  {form.image && (
                    <button type="button" className="lf-modal__preview-clear" onClick={clearNewImage}>
                      <i className="fa fa-times" aria-hidden="true" />
                      {t("delete")}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="lf-modal__foot">
            <button type="button" className="lf-modal__btn lf-modal__btn--ghost" onClick={onClose}>
              {t("cancel")}
            </button>
            <button type="submit" className="lf-modal__btn lf-modal__btn--primary" disabled={submitting}>
              {submitting ? (
                <>
                  <span className="lf-spinner lf-spinner--btn" aria-hidden="true" />
                  {t("updating")}
                </>
              ) : (
                <>
                  <i className="fa fa-check" aria-hidden="true" />
                  {isEdit ? t("lostFoundSaveChanges") : t("lostFoundPublish")}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

function ItemDetailsModal({ open, item, onClose, onEdit, t, locale }) {
  if (!open || !item) return null;

  const isReturned = item.status === "returned";
  const description = String(item.description || "").trim();

  return createPortal(
    <div className="lf-modal-backdrop" onClick={onClose}>
      <div
        className="lf-modal lf-details"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lf-details-title"
      >
        <div className="lf-details__head">
          <div>
            <p className="lf-details__code">{formatItemCode(item._id)}</p>
            <h2 id="lf-details-title">{item.title || t("lostFoundItemDetails")}</h2>
          </div>
          <button
            type="button"
            className="lf-modal__close"
            onClick={onClose}
            aria-label={t("cancel")}
          >
            <i className="fa fa-times" />
          </button>
        </div>

        <div className="lf-details__body">
          <div className="lf-details__media">
            {item.image ? (
              <img
                src={`${config.UPLOADS_SERVER}/lost-found/${item.image}`}
                alt={item.title || ""}
              />
            ) : (
              <div className="lf-details__placeholder">
                <i className="fa fa-image" aria-hidden="true" />
                <span>{t("lostFoundNoImage")}</span>
              </div>
            )}
          </div>

          <div className="lf-details__meta">
            <div className="lf-details__row">
              <span className="lf-details__label">
                <i className="fa fa-calendar" aria-hidden="true" />
                {t("lostFoundDate")}
              </span>
              <span className="lf-details__value">{formatDate(item.foundDate, locale)}</span>
            </div>
            <div className="lf-details__row">
              <span className="lf-details__label">
                <i className="fa fa-flag" aria-hidden="true" />
                {t("status")}
              </span>
              <span className={`lf-details__badge ${isReturned ? "is-returned" : "is-found"}`}>
                <span className="lf-status-dot" aria-hidden="true" />
                {isReturned ? t("lostFoundReturned") : t("lostFoundFound")}
              </span>
            </div>
          </div>

          <div className="lf-details__section">
            <span className="lf-details__label">
              <i className="fa fa-align-left" aria-hidden="true" />
              {t("description")}
            </span>
            <p className="lf-details__text">
              {description || t("lostFoundNoDescription")}
            </p>
          </div>
        </div>

        <div className="lf-details__foot">
          <button type="button" className="lf-modal__btn lf-modal__btn--ghost" onClick={onClose}>
            {t("close")}
          </button>
          {onEdit && (
            <button
              type="button"
              className="lf-modal__btn lf-modal__btn--primary"
              onClick={() => onEdit(item)}
            >
              <i className="fa fa-pencil" aria-hidden="true" />
              {t("edit")}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

const LostFound = () => {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [archivedCount, setArchivedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [sortBy, setSortBy] = useState("foundDate-desc");
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);

  const openCreateModal = () => {
    setViewingItem(null);
    setEditingItem(null);
    setModalOpen(true);
  };

  const openEditModal = (item) => {
    setViewingItem(null);
    setEditingItem(item);
    setModalOpen(true);
  };

  const openViewModal = (item) => {
    setViewingItem(item);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(null);
  };

  const closeViewModal = () => {
    setViewingItem(null);
  };

  const handleEditFromDetails = (item) => {
    setViewingItem(null);
    openEditModal(item);
  };

  const loadItems = async () => {
    setLoading(true);
    try {
      const [active, archived] = await Promise.all([
        fetchLostFoundItems(),
        fetchLostFoundItems({ archived: true }),
      ]);
      setItems(Array.isArray(active) ? active : []);
      setArchivedCount(Array.isArray(archived) ? archived.length : 0);
    } catch (error) {
      toast.error(error?.message || t("lostFoundLoadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, dateFrom, dateTo, sortBy]);

  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    const fromKey = toLocalDateKey(dateFrom);
    const toKey = toLocalDateKey(dateTo);
    return items.filter((item) => {
      const matchesStatus = !statusFilter || item.status === statusFilter;
      if (!matchesStatus) return false;

      if (fromKey || toKey) {
        const foundKey = toLocalDateKey(item.foundDate);
        if (!foundKey) return false;
        if (fromKey && foundKey < fromKey) return false;
        if (toKey && foundKey > toKey) return false;
      }

      if (!term) return true;
      const name = String(item.title || "").toLowerCase();
      const description = String(item.description || "").toLowerCase();
      return name.includes(term) || description.includes(term);
    });
  }, [items, search, statusFilter, dateFrom, dateTo]);

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates || [null, null];
    setDateFrom(start);
    setDateTo(end);
  };

  const sortedItems = useMemo(() => sortItems(filteredItems, sortBy), [filteredItems, sortBy]);
  const totalPages = Math.max(1, Math.ceil(sortedItems.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return sortedItems.slice(start, start + PAGE_SIZE);
  }, [sortedItems, currentPage]);

  const stats = useMemo(() => {
    const weekStart = startOfWeek();
    const foundCount = items.filter((item) => item.status !== "returned").length;
    const addedThisWeek = items.filter((item) => {
      const created = new Date(item.createdAt || item.foundDate || 0);
      return !Number.isNaN(created.getTime()) && created >= weekStart;
    }).length;
    return {
      total: items.length,
      found: foundCount,
      thisWeek: addedThisWeek,
      archived: archivedCount,
    };
  }, [items, archivedCount]);

  const handleArchive = async (id) => {
    const ok = await swal({
      title: t("areYouSure") || "Are you sure?",
      text: t("lostFoundArchiveConfirm"),
      icon: "warning",
      buttons: [t("cancel"), t("confirm")],
    });
    if (!ok) return;

    try {
      await archiveLostFoundItem(id);
      toast.success(t("lostFoundArchived"));
      await loadItems();
    } catch (error) {
      toast.error(error?.message || t("lostFoundArchiveError"));
    }
  };

  const changeStatus = async (item, nextStatus) => {
    if (!nextStatus || nextStatus === item.status) return;

    const statusLabel =
      nextStatus === "returned" ? t("lostFoundReturned") : t("lostFoundFound");

    const ok = await swal({
      title: t("areYouSure") || "Are you sure?",
      text: t("lostFoundStatusChangeConfirm", {
        item: item.title,
        status: statusLabel,
      }),
      icon: "warning",
      buttons: [t("cancel"), t("confirm")],
    });
    if (!ok) return;

    try {
      await updateLostFoundItemStatus(item._id, nextStatus);
      setItems((prev) =>
        prev.map((entry) =>
          entry._id === item._id ? { ...entry, status: nextStatus } : entry
        )
      );
      toast.success(
        t("lostFoundStatusUpdated", {
          item: item.title,
          status: statusLabel,
        })
      );
    } catch (error) {
      toast.error(error?.message || t("lostFoundUpdateError"));
    }
  };

  return (
    <Fragment>
      <PageTitle activeMenu={t("lostFound")} motherMenu={t("navCustomerSupport")} />

      <div className="lf-page">
        <div className="lf-toolbar">
          <div>
            <h3 className="lf-title">{t("lostFound")}</h3>
            <p className="lf-subtitle">
              <span className="lf-subtitle__dot" aria-hidden="true" />
              {t("lostFoundPageSubtitle")}
            </p>
          </div>
          <button type="button" className="lf-btn lf-btn--primary" onClick={openCreateModal}>
            <i className="fa fa-plus" aria-hidden="true" />
            {t("lostFoundAddItem")}
          </button>
        </div>

        <div className="lf-stats">
          <div className="lf-stat">
            <span className="lf-stat__icon is-total" aria-hidden="true">
              <i className="fa fa-briefcase" />
            </span>
            <div>
              <strong>{stats.total}</strong>
              <span>{t("lostFoundStatTotal")}</span>
            </div>
          </div>
          <div className="lf-stat">
            <span className="lf-stat__icon is-found" aria-hidden="true">
              <i className="fa fa-search" />
            </span>
            <div>
              <strong>{stats.found}</strong>
              <span>{t("lostFoundStatFound")}</span>
            </div>
          </div>
          <div className="lf-stat">
            <span className="lf-stat__icon is-week" aria-hidden="true">
              <i className="fa fa-calendar" />
            </span>
            <div>
              <strong>{stats.thisWeek}</strong>
              <span>{t("lostFoundStatThisWeek")}</span>
            </div>
          </div>
          <div className="lf-stat">
            <span className="lf-stat__icon is-archived" aria-hidden="true">
              <i className="fa fa-archive" />
            </span>
            <div>
              <strong>{stats.archived}</strong>
              <span>{t("lostFoundStatArchived")}</span>
            </div>
          </div>
        </div>

        <div className="lf-card">
          <div className="lf-filters">
            <div className="lf-search">
              <i className="fa fa-search" aria-hidden="true" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("lostFoundSearchPlaceholder")}
              />
            </div>
            <select
              className="lf-input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">{t("lostFoundAllStatus")}</option>
              <option value="found">{t("lostFoundFound")}</option>
              <option value="returned">{t("lostFoundReturned")}</option>
            </select>
            <div
              className={`lf-date-range${dateFrom || dateTo ? " is-active" : ""}`}
              aria-label={t("lostFoundDateRange")}
            >
              <i className="fa fa-calendar" aria-hidden="true" />
              <DatePicker
                selected={dateFrom}
                onChange={handleDateRangeChange}
                startDate={dateFrom}
                endDate={dateTo}
                selectsRange
                maxDate={new Date()}
                placeholderText={t("lostFoundDateRangePlaceholder")}
                className="lf-date-range__input"
                dateFormat="dd/MM/yyyy"
                isClearable
                showPopperArrow={false}
              />
            </div>
            <select className="lf-input" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="foundDate-desc">{t("lostFoundSortDateNewest")}</option>
              <option value="foundDate-asc">{t("lostFoundSortDateOldest")}</option>
              <option value="title-asc">{t("lostFoundSortTitleAZ")}</option>
              <option value="title-desc">{t("lostFoundSortTitleZA")}</option>
              <option value="status-asc">{t("lostFoundSortStatus")}</option>
            </select>
          </div>

          {loading ? (
            <div className="lf-loading">
              <span className="lf-spinner" aria-hidden="true" />
              {t("loading")}...
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table lf-table mb-0">
                  <thead>
                    <tr>
                      <th>
                        <span className="lf-th">
                          <i className="fa fa-cube" aria-hidden="true" />
                          {t("lostFoundColItem")}
                        </span>
                      </th>
                      <th>
                        <span className="lf-th">
                          <i className="fa fa-calendar" aria-hidden="true" />
                          {t("lostFoundDate")}
                        </span>
                      </th>
                      <th>
                        <span className="lf-th">
                          <i className="fa fa-align-left" aria-hidden="true" />
                          {t("description")}
                        </span>
                      </th>
                      <th>
                        <span className="lf-th">
                          <i className="fa fa-flag" aria-hidden="true" />
                          {t("status")}
                        </span>
                      </th>
                      <th className="text-end">
                        <span className="lf-th lf-th--end">
                          <i className="fa fa-ellipsis-h" aria-hidden="true" />
                          {t("actions")}
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedItems.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="lf-empty">
                          <i className="fa fa-inbox" aria-hidden="true" />
                          <span>{t("noDataFound")}</span>
                        </td>
                      </tr>
                    ) : (
                      paginatedItems.map((item) => {
                        const isReturned = item.status === "returned";
                        return (
                          <tr key={item._id} className={isReturned ? "is-returned" : "is-found"}>
                            <td>
                              <div className="lf-item-cell">
                                <div className="lf-thumb">
                                  {item.image ? (
                                    <img
                                      src={`${config.UPLOADS_SERVER}/lost-found/${item.image}`}
                                      alt={item.title}
                                    />
                                  ) : (
                                    <i className="fa fa-image" aria-hidden="true" />
                                  )}
                                </div>
                                <div className="lf-item-meta">
                                  <span className="lf-name">{item.title}</span>
                                  <span className="lf-code">{formatItemCode(item._id)}</span>
                                </div>
                              </div>
                            </td>
                            <td className="lf-date">{formatDate(item.foundDate, i18n.language)}</td>
                            <td className="lf-desc" title={item.description || ""}>
                              {truncateText(item.description, 48)}
                            </td>
                            <td>
                              <div className={`lf-status-wrap ${isReturned ? "is-returned" : "is-found"}`}>
                                <span className="lf-status-dot" aria-hidden="true" />
                                <select
                                  className="lf-status"
                                  value={isReturned ? "returned" : "found"}
                                  onChange={(e) => changeStatus(item, e.target.value)}
                                  aria-label={t("status")}
                                >
                                  <option value="found">{t("lostFoundFound")}</option>
                                  <option value="returned">{t("lostFoundReturned")}</option>
                                </select>
                              </div>
                            </td>
                            <td className="text-end">
                              <div className="lf-row-actions">
                                <button
                                  type="button"
                                  className="lf-icon-btn lf-icon-btn--view"
                                  onClick={() => openViewModal(item)}
                                  aria-label={t("lostFoundViewDetails")}
                                  title={t("lostFoundViewDetails")}
                                >
                                  <i className="fa fa-eye" aria-hidden="true" />
                                </button>
                                <button
                                  type="button"
                                  className="lf-icon-btn lf-icon-btn--edit"
                                  onClick={() => openEditModal(item)}
                                  aria-label={t("edit")}
                                  title={t("edit")}
                                >
                                  <i className="fa fa-pencil" aria-hidden="true" />
                                </button>
                                <button
                                  type="button"
                                  className="lf-icon-btn lf-icon-btn--archive"
                                  onClick={() => handleArchive(item._id)}
                                  aria-label={t("lostFoundArchive")}
                                  title={t("lostFoundArchive")}
                                >
                                  <i className="fa fa-archive" aria-hidden="true" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {sortedItems.length > 0 && (
                <div className="lf-pagination">
                  <span>
                    {t("lostFoundShowingCount", {
                      shown: paginatedItems.length,
                      total: sortedItems.length,
                    })}
                  </span>
                  <div className="lf-pagination__btns">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                      <button
                        key={n}
                        type="button"
                        className={`lf-page-btn${currentPage === n ? " is-active" : ""}`}
                        onClick={() => setPage(n)}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <ItemFormModal
        open={modalOpen}
        item={editingItem}
        onClose={closeModal}
        onSaved={loadItems}
        t={t}
      />

      <ItemDetailsModal
        open={Boolean(viewingItem)}
        item={viewingItem}
        onClose={closeViewModal}
        onEdit={handleEditFromDetails}
        t={t}
        locale={i18n.language}
      />
    </Fragment>
  );
};

export default LostFound;
